import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PORTFOLIO } from "../constants/testIds";
import { PORTFOLIO_ITEMS } from "../lib/site-data";

const FILTERS = [
  { value: "all", label: "All Work", testid: PORTFOLIO.filterAll },
  { value: "hydro", label: "Hydro Dip", testid: PORTFOLIO.filterHydro },
  { value: "dash", label: "Dashboards", testid: PORTFOLIO.filterDash },
  { value: "custom", label: "Custom Builds", testid: PORTFOLIO.filterCustom },
];

export default function Portfolio() {
  const [filter, setFilter] = useState("all");

  const items = useMemo(() => {
    if (filter === "all") return PORTFOLIO_ITEMS;
    return PORTFOLIO_ITEMS.filter((p) => p.tag === filter);
  }, [filter]);

  return (
    <div data-testid={PORTFOLIO.root}>
      <section className="bg-[color:var(--brand-navy)] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-noise opacity-20" />
        <div className="container-tg relative py-24 lg:py-32">
          <div className="overline text-[color:var(--brand-yellow)] mb-6">Portfolio</div>
          <h1 className="font-display text-5xl lg:text-7xl font-extrabold tracking-tighter leading-none max-w-4xl">
            Work that lives on the water.
          </h1>
          <p className="mt-8 max-w-2xl text-white/80 text-lg leading-relaxed">
            A selection of dashboards, panels and custom finishes built for
            OEM partners and individual boat owners across the world.
          </p>
        </div>
      </section>

      <section className="container-tg py-12 border-b border-[color:var(--surface-border)]">
        <div className="flex flex-wrap items-center gap-3">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              data-testid={f.testid}
              onClick={() => setFilter(f.value)}
              className={`px-5 py-2.5 text-xs uppercase tracking-widest font-bold border transition-all ${
                filter === f.value
                  ? "bg-[color:var(--brand-navy)] text-white border-[color:var(--brand-navy)]"
                  : "bg-white text-slate-600 border-[color:var(--surface-border)] hover:border-[color:var(--brand-navy)] hover:text-[color:var(--brand-navy)]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </section>

      <section data-testid={PORTFOLIO.grid} className="container-tg py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((p) => (
            <article key={p.id} className="group bg-white border border-[color:var(--surface-border)] overflow-hidden hover:shadow-xl transition-all">
              <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              <div className="p-6">
                <div className="overline mb-2 text-[color:var(--brand-navy)]/70">{p.category}</div>
                <h3 className="font-display text-xl font-bold text-[color:var(--brand-navy)]">
                  {p.title}
                </h3>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="container-tg pb-24">
        <div className="bg-white border border-[color:var(--surface-border)] p-10 lg:p-14 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <div>
            <h3 className="font-display text-3xl lg:text-4xl font-extrabold text-[color:var(--brand-navy)] tracking-tight">
              Have a project in mind?
            </h3>
            <p className="mt-2 text-slate-600 max-w-lg">
              Share a few photos and your boat model — we'll get back with a
              scoped quote.
            </p>
          </div>
          <Link to="/contact" className="btn-primary shrink-0">
            Get a Quote <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
